// Past the component loader's retries and the wc-loader's, so a slow load that
// recovers isn't reported as a failure.
export const HEADER_LOAD_TIMEOUT_MS = 15000;
// Both modals render from the store's state as soon as it changes.
export const SIGN_IN_TIMEOUT_MS = 1000;

/**
 * Reports a header failure to Datadog RUM. RUM runs only on pages whose app
 * starts it, so this is a no-op everywhere else.
 *
 * @param {string} message
 * @param {Object} [attributes] - extra context, searchable as @context.*
 */
export const reportHeaderError = (message, attributes) =>
  window.DD_RUM?.addError?.(new Error(message), {
    feature: 'header',
    ...attributes,
  });

// A page that can't run one frame in this long isn't rendering anything.
export const FRAME_WAIT_MS = 1000;

/**
 * Remembers whether the tab was ever hidden after the header mounted, for the
 * load report: frames don't run in a hidden tab, so a header that only had
 * the foreground for a moment is a different finding from one that had it
 * the whole time.
 *
 * @returns {{ wasHidden: function(): boolean, stop: function(): void }}
 */
export const watchHeaderLoad = () => {
  let wasHidden = document.visibilityState === 'hidden';
  const onVisibilityChange = () => {
    if (document.visibilityState === 'hidden') wasHidden = true;
  };
  document.addEventListener('visibilitychange', onVisibilityChange);
  return {
    wasHidden: () => wasHidden,
    stop: () =>
      document.removeEventListener('visibilitychange', onVisibilityChange),
  };
};

/**
 * A chunk failure is already reported by the wc-loader; this reports the
 * result, a header that never rendered.
 *
 * Components render in animation frames, which a hidden tab never runs and
 * some web views pause without reporting the page hidden. So the check keeps
 * looking on each frame for a second before it reports: a header that
 * renders in that time stays quiet (its first render can still be waiting
 * on a chunk), and a page that ran no frame at all is skipped, the way a
 * hidden tab is.
 *
 * When the web-components entry never executes (an import of its own that
 * fails, say), nothing defines any component and nothing reports it; this
 * timer is the only signal. That page-wide failure is reported under its own
 * message and feature, so header queries and monitors never count it, and
 * "Header failed to load" means only a header that didn't hydrate on a page
 * whose runtime was up.
 *
 * @param {HTMLElement} header - the va-header-navigation element
 * @param {{ wasHidden: function(): boolean }} watch - from watchHeaderLoad
 */
export const reportHeaderNotLoaded = (header, watch) => {
  const hydrated = () => header?.classList.contains('hydrated');
  if (document.visibilityState === 'hidden' || hydrated()) return;
  let frames = 0;
  let pending;
  const giveUp = setTimeout(() => {
    cancelAnimationFrame(pending);
    if (frames === 0 || hydrated()) return;
    const context = { wasHidden: watch.wasHidden() };
    if (window.customElements.get('va-header-navigation')) {
      reportHeaderError('Header failed to load', context);
    } else {
      reportHeaderError('Web components runtime not loaded', {
        ...context,
        feature: 'web-components',
        reportedBy: 'header',
      });
    }
  }, FRAME_WAIT_MS);
  const check = () => {
    frames += 1;
    if (hydrated()) {
      clearTimeout(giveUp);
    } else {
      pending = requestAnimationFrame(check);
    }
  };
  pending = requestAnimationFrame(check);
};

export const reportSignInNotOpened = () => {
  if (!document.querySelector('#signin-signup-modal, #form-sign-in-modal')) {
    reportHeaderError('Header sign in opened no modal');
  }
};
